<script>
import { mapGetters } from 'vuex';
import AutomationFlowSessionsAPI from 'dashboard/api/automationFlowSessions';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import {
  flowVersionLabel,
  flowVersionRoute,
} from 'dashboard/helper/flowVersionLabel';
import { FLOW_EDIT_PERMISSIONS } from 'dashboard/routes/dashboard/settings/automationFlows/automationFlows.routes';
import PanelEmptyState from '../sidebar/PanelEmptyState.vue';

// Tamanho da página quando a resposta não diz o dela. O número que vale é o
// `meta.per_page` do servidor (GET .../automation_flow_sessions, 5 hoje): ler
// de lá evita dois números soltos que divergem quando um lado muda.
const FALLBACK_PAGE_SIZE = 5;

const STATUS_KEYS = {
  running: 'AUTOMATION_FLOWS.EXECUTIONS.STATUS.RUNNING',
  waiting_timer: 'AUTOMATION_FLOWS.EXECUTIONS.STATUS.WAITING_TIMER',
  waiting_input: 'AUTOMATION_FLOWS.EXECUTIONS.STATUS.WAITING_INPUT',
  completed: 'AUTOMATION_FLOWS.EXECUTIONS.STATUS.COMPLETED',
  failed: 'AUTOMATION_FLOWS.EXECUTIONS.STATUS.FAILED',
  canceled: 'AUTOMATION_FLOWS.EXECUTIONS.STATUS.CANCELED',
};

// "Que fluxos já rodaram aqui, e em qual versão?" (#1705). Listagem simples e
// paginada, no fim do painel da conversa e da página do contato.
//
// Passa-se UM dos dois: `conversationId` (display_id, o da URL) ou `contactId`.
//
// `active` existe porque o painel da conversa monta as duas abas de uma vez
// (`v-show`): sem ele a aba escondida buscaria a cada conversa aberta. A busca
// acontece na primeira vez que a lista fica visível, e de novo quando o dono
// muda com ela visível.
export default {
  name: 'FlowRunHistory',
  components: { PanelEmptyState },
  props: {
    conversationId: { type: [Number, String], default: null },
    contactId: { type: [Number, String], default: null },
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      sessions: [],
      meta: { count: 0, current_page: 1 },
      page: 1,
      isLoading: false,
      loadFailed: false,
      // Chave do dono da última busca: evita buscar de novo ao só alternar abas.
      loadedFor: null,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    ownerKey() {
      if (this.conversationId) return `conversation:${this.conversationId}`;
      if (this.contactId) return `contact:${this.contactId}`;
      return null;
    },
    isContactList() {
      return !this.conversationId && !!this.contactId;
    },
    pageSize() {
      return Number(this.meta.per_page) || FALLBACK_PAGE_SIZE;
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil((Number(this.meta.count) || 0) / this.pageSize)
      );
    },
    // O editor é rota de quem edita fluxo; link para rota que o usuário não
    // abre seria link quebrado, mesmo com o fluxo acessível pela policy.
    canOpenEditor() {
      const permissions = getUserPermissions(this.currentUser, this.accountId);
      return hasPermissions(FLOW_EDIT_PERMISSIONS, permissions);
    },
  },
  watch: {
    ownerKey() {
      this.page = 1;
      this.loadedFor = null;
      this.fetchIfVisible();
    },
    active() {
      this.fetchIfVisible();
    },
  },
  mounted() {
    this.fetchIfVisible();
  },
  methods: {
    fetchIfVisible() {
      if (!this.active || !this.ownerKey) return;
      if (this.loadedFor === this.ownerKey) return;
      this.fetch();
    },
    request() {
      const params = { page: this.page };
      if (this.conversationId) {
        return AutomationFlowSessionsAPI.byConversation(
          this.conversationId,
          params
        );
      }
      return AutomationFlowSessionsAPI.byContact(this.contactId, params);
    },
    async fetch() {
      const owner = this.ownerKey;
      this.isLoading = true;
      this.loadFailed = false;
      try {
        const { data } = await this.request();
        // Resposta de um dono anterior (o usuário trocou de conversa no meio).
        if (owner !== this.ownerKey) return;
        this.sessions = (data && data.payload) || [];
        this.meta = (data && data.meta) || {
          count: 0,
          current_page: this.page,
        };
        this.loadedFor = owner;
      } catch (error) {
        if (owner !== this.ownerKey) return;
        this.sessions = [];
        this.loadFailed = true;
      } finally {
        if (owner === this.ownerKey) this.isLoading = false;
      }
    },
    goTo(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.fetch();
    },
    statusLabel(session) {
      const key = STATUS_KEYS[session.status];
      return key ? this.$t(key) : session.status;
    },
    versionLabel(session) {
      return flowVersionLabel(session.version, this.$t.bind(this));
    },
    when(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp * 1000).toLocaleString('pt-BR');
    },
    flowName(session) {
      return (
        (session.flow && session.flow.name) ||
        this.$t('AUTOMATION_FLOWS.RUN_HISTORY.DELETED_FLOW')
      );
    },
    // Execução numa versão PUBLICADA abre o fluxo naquela versão (somente
    // leitura): é ela que explica o que aconteceu na conversa, não o rascunho de
    // hoje. Execução de teste — e linha sem versão — segue indo para o editor.
    // As duas rotas exigem as mesmas permissões, então a porta é a mesma.
    flowRoute(session) {
      if (!this.canOpenEditor || !session.flow || !session.flow.accessible) {
        return null;
      }
      const versionRoute = flowVersionRoute({
        accountId: this.accountId,
        flowId: session.flow.id,
        version: session.version,
      });
      if (versionRoute) return versionRoute;
      return {
        name: 'automation_flows_edit',
        params: { accountId: this.accountId, flowId: session.flow.id },
      };
    },
    conversationRoute(session) {
      return {
        name: 'inbox_conversation',
        params: {
          accountId: this.accountId,
          conversation_id: session.conversation_id,
        },
      };
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <p v-if="isLoading" class="m-0 text-xs text-muted">
      {{ $t('AUTOMATION_FLOWS.RUN_HISTORY.LOADING') }}
    </p>
    <p v-else-if="loadFailed" class="m-0 text-xs text-muted">
      {{ $t('AUTOMATION_FLOWS.RUN_HISTORY.LOAD_FAILED') }}
    </p>
    <PanelEmptyState
      v-else-if="!sessions.length"
      :message="$t('AUTOMATION_FLOWS.RUN_HISTORY.EMPTY')"
    />

    <template v-else>
      <ul class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="session in sessions"
          :key="session.id"
          class="flex flex-col gap-0.5 px-3 py-2 border rounded-lg border-line-faint"
        >
          <div class="flex items-center min-w-0 gap-2">
            <router-link
              v-if="flowRoute(session)"
              :to="flowRoute(session)"
              class="text-xs font-semibold truncate text-flipdesk-500"
            >
              {{ flowName(session) }}
            </router-link>
            <span
              v-else
              class="text-xs font-semibold truncate text-body"
              :title="
                session.flow && !session.flow.accessible
                  ? $t('AUTOMATION_FLOWS.RUN_HISTORY.RESTRICTED')
                  : ''
              "
            >
              {{ flowName(session) }}
            </span>
            <span
              v-if="versionLabel(session)"
              class="text-xs shrink-0 text-muted"
            >
              {{ versionLabel(session) }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-x-2 text-xs text-muted">
            <span>{{ statusLabel(session) }}</span>
            <span>{{ when(session.created_at) }}</span>
            <router-link
              v-if="isContactList && session.conversation_id"
              :to="conversationRoute(session)"
              class="text-xs text-flipdesk-500"
            >
              {{
                $t('AUTOMATION_FLOWS.EXECUTIONS.CONVERSATION', {
                  id: session.conversation_id,
                })
              }}
            </router-link>
          </div>
          <p
            v-if="session.error_message"
            class="m-0 text-xs truncate text-ruby-800"
            :title="session.error_message"
          >
            {{ session.error_message }}
          </p>
        </li>
      </ul>

      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between gap-2 text-xs text-muted"
      >
        <button
          type="button"
          class="px-2 py-1 border rounded-md border-line disabled:opacity-50"
          :disabled="page <= 1 || isLoading"
          @click="goTo(page - 1)"
        >
          {{ $t('AUTOMATION_FLOWS.RUN_HISTORY.PREVIOUS') }}
        </button>
        <span>
          {{
            $t('AUTOMATION_FLOWS.RUN_HISTORY.PAGE', {
              page,
              total: totalPages,
            })
          }}
        </span>
        <button
          type="button"
          class="px-2 py-1 border rounded-md border-line disabled:opacity-50"
          :disabled="page >= totalPages || isLoading"
          @click="goTo(page + 1)"
        >
          {{ $t('AUTOMATION_FLOWS.RUN_HISTORY.NEXT') }}
        </button>
      </div>
    </template>
  </div>
</template>
